
import React, {useState, useEffect, useMemo, useContext} from 'react';
import { connect } from "react-redux";

import logo from "../../images/cc_logo_white.svg";
import './Calculator.css';
import QuestionsConfig from './QuestionsConfig.js';
import { calculateFootprint } from './Calculations.js';
import CountrySelectionModal from "../common/CountrySelectionModal";
import CountrySelectionStatus from "../common/CountrySelectionStatus";
import InfoBox from "../common/InfoBox";
import {getConfigByMerchantCode} from './merchant-config/merchantConfig';
import { useLocation } from "react-router-dom";

import Results from './results/Results'
import CCSlider from './CCSlider';
import NumberPicker from './NumberPicker';
import { ThemeProvider, Select } from '@material-ui/core';
import { createMuiTheme } from '@material-ui/core/styles';
import { getCountryName } from "../../util/countryCodes";
import {Checkbox, Popover} from "antd";
import { hotjar } from 'react-hotjar';
import ReactGA from 'react-ga';
import * as drivingData from '../../util/drivingData';

import PropTypes from "prop-types";
import { bindActionCreators } from 'redux';
import * as personalFootprintActions from "../../redux/actions/personalFootprintActions";
import {
  buildAnswerObject
} from "../../util/personalCalculatorAnswerUtils";
import UserInfoContext from "../../util/UserInfoContext";
import UIContext from "../../util/UIContext";

const theme = createMuiTheme({
  palette: {
    primary: {
      main: '#2aa43c'
    },
    secondary: {
      main: '#444'
    }
  }
});

//at time of writing, country, userSubscriptions only provided when accessed through logged in view; BUT, it also comes from redux
const Calculator = ({ countryCodeFromRedux, country, userSubscriptions, personalFootprintData, actions, currentUserCognito, ...props }) => {
  const location = useLocation();

  const [isShowCountryModal, setShowCountryModal] = useState(false);
  const [questionsConfig, setQuestionsConfig] = useState();
  const [merchantConfig, setMerchantConfig] = useState();
  const [countryCode, setCountryCode] = useState();

  const params = new URLSearchParams(window.location.search)
  const countryWasChangedByUser = params.has("countryChangedByUser")
  const isHideSave = 'true' === params.get("hideSave")
  const isHideHeader = ('true' === params.get("hideHeader")) || merchantConfig?.showHeader===false;

  const uiContext = useContext(UIContext);
  const {userInfo} = useContext(UserInfoContext);

  const isMyCCDashboard = location.pathname.includes('/my-cc');

  useEffect(() => {
    const referrerMerchantCode = userInfo?.referrerMerchant?.code;
    setMerchantConfig(getConfigByMerchantCode(referrerMerchantCode));
    }, [userInfo]);

  useEffect(() => {
    setCountryCode(personalFootprintData?.answers?.footprintDataRegionCode || merchantConfig?.countryOverride || country || countryCodeFromRedux);
  }, [countryCodeFromRedux, country, merchantConfig, personalFootprintData]);

  useEffect(() => {
    if(countryCode){
      setQuestionsConfig(new QuestionsConfig(countryCode));
    }
  }, [countryCode]);

  useEffect(() => {
    if (!countryWasChangedByUser && currentUserCognito?.isSignedIn) {
      actions.getAnswersByEmail(currentUserCognito?.authData?.email)
    }
  }, [currentUserCognito])

  useEffect(() => {
    hotjar.initialize(process.env.HOTJAR_SITE_ID, 6);
    ReactGA.pageview("/calculator");
  }, []);

  const [footprint, setFootprint] = useState();
  const [footprintOfOneAdult, setFootprintOfOneAdult] = useState();

  const [adults] = useState(1);
  const [children] = useState(0);

  const [carFuel, setCarFuel] = useState('PETROL');
  const [carSize, setCarSize] = useState('MEDIUM');
  const [driving, setDriving] = useState();
  const [diet, setDiet] = useState();
  const [energy, setEnergy] = useState();
  const [waste, setWaste] = useState();

  const [quickFlights, setQuickFlights] = useState(0);
  const [shortFlights, setShortFlights] = useState(0);
  const [intermediateFlights, setIntermediateFlights] = useState(0);
  const [longFlights, setLongFlights] = useState(0);
  
  const [hideRecommendedSubscriptionPanel, setRecommendedSubscriptionPanel] = useState(false);
  const [isRenewableEnergy, setIsRenewableEnergy] = useState(false);

  const allAnswers = useMemo(() => {
      return buildAnswerObject({
            adults, children,
            diet,
            energy, currency: questionsConfig?.questions?.energy?.unit, isRenewableEnergy,
            driving, carSize, carFuel,
            waste,
            shortFlights, quickFlights, intermediateFlights, longFlights,
            countryCode, storedCountryCode: countryCodeFromRedux
      });
    }, [questionsConfig, diet, driving, energy, waste, quickFlights, shortFlights, intermediateFlights, longFlights, adults, children, carFuel, carSize, isRenewableEnergy]);

  useEffect(() => {
    if (questionsConfig && diet) {
      setFootprint(calculateFootprint({questions: questionsConfig, answers: allAnswers}));
      setFootprintOfOneAdult(calculateFootprint({questions: questionsConfig, answers: allAnswers, calculateForOneAdult:true}));
    }
  }, [questionsConfig, diet, driving, energy, waste, quickFlights, shortFlights, intermediateFlights, longFlights, adults, children, carFuel, carSize, isRenewableEnergy]);

  useEffect(() => {
    const userAnswers = personalFootprintData?.answers;
    if (userAnswers && userAnswers.type !== 'error' && userAnswers.schemaVersion === 3) {
      // setAdults(userAnswers.HOUSEHOLD.adults)
      // setChildren(userAnswers.HOUSEHOLD.children)
      setCarSize(userAnswers.DRIVING[0].size)
      setCarFuel(userAnswers.DRIVING[0].fuel)
      setQuickFlights(userAnswers.FLIGHTS.numQuick)
      setShortFlights(userAnswers.FLIGHTS.numShort)
      setIntermediateFlights(userAnswers.FLIGHTS.numIntermediate)
      setLongFlights(userAnswers.FLIGHTS.numLong)
      setIsRenewableEnergy(!!userAnswers.ENERGY.hasCarbonNeutralEnergy)
    }
  }, [personalFootprintData])

  useEffect(() => {
    const userAnswers = personalFootprintData?.answers;
    if ((personalFootprintData || countryWasChangedByUser ||!currentUserCognito?.isSignedIn) && questionsConfig) {
      const drivingValue = (userAnswers?.DRIVING ? userAnswers?.DRIVING[0]?.yearlyDistance?.value : null);
      const energyValue = userAnswers?.ENERGY?.monthlySpend?.value;
      setDriving((drivingValue === undefined || drivingValue === null) ? questionsConfig.questions.driving.defaultValue : drivingValue);
      setDiet(userAnswers?.DIET?.yearlyCarbonFootprintOneAdult?.value || questionsConfig.questions.diet.defaultValue);
      setEnergy((energyValue === undefined || energyValue === null) ? questionsConfig.questions.energy.defaultValue : energyValue);
      setWaste(userAnswers?.WASTE?.yearlyProducedOneAdult?.value || questionsConfig.questions.waste.defaultValue);
    }
  }, [personalFootprintData, questionsConfig, currentUserCognito]);

  //tried a few things here and couldn't come up with another way :(
  const handleQuestionChange = (event, value, questionAlias, rootAlias) => {
    switch (rootAlias) {
      case 'DIET': setDiet(value); break;
      case 'DRIVING': setDriving(value); break;
      case 'carType':
        setCarSize(value.split('-')[0]);
        setCarFuel(value.split('-')[1]);
        break;
      case 'ENERGY': setEnergy(value); break;
      case 'WASTE': setWaste(value); break;
    }
  }

  const businessFlightPopoverContent =
    <>
      Do you travel for business? We encourage you to engage with your company about offsetting business travel.<br />Contact CarbonClick if you&apos;d like any more information.
    </>;

  useEffect(() => {
    if (props.userSubscription && props.userSubscription[0] && props.userSubscription[0].id)
      setRecommendedSubscriptionPanel(true);
  }, [props.userSubscription] )

  return (
    <ThemeProvider theme={theme}>
      <main className="cc-main-content calculator">
        <article className='centered-content'>
          {
            !merchantConfig?.countryOverride &&
            
            <div style={{ marginBottom: '8px' }}>
            {countryCode && <CountrySelectionStatus country={countryCode} userSubscriptions={userSubscriptions} setShowModal={setShowCountryModal} /> }
          </div>
          }
        { !isHideHeader &&
          <section
            className='calculator-header'
            style={{ backgroundImage: uiContext.theme.images.hero.main}}
          >
            <div className='calculator-header-name'>{`My Carbon Footprint`}<br />
              <span style={{ fontSize: 18 }}>{!isMyCCDashboard && "By"}
                <a target="_blank" rel="noopener noreferrer" href={uiContext?.content?.link}>
                  <img
                    className="cc-logo"
                    src={ isMyCCDashboard ? uiContext?.content?.logo?.light : logo }
                    alt="carbonclick"
                    style={{ maxHeight: "80px" }}
                  />
                </a>
              </span>
            </div>

          </section>
          }
          <div className="App-main">

            {diet &&
              <div className="main-pane" style={{ marginBottom: 15 }}>
                <div className='calculator-title'>Use our simple calculator to calculate your carbon footprint </div>
                <div className='calculator-learn-more'>
                  <ReactGA.OutboundLink eventLabel="WhatIsCarbonFootprint" to="https://carbonclick.com/what-is-a-carbon-footprint/" target="_blank" >
                    What is a Carbon Footprint?
              </ReactGA.OutboundLink >
                </div>

                {questionsConfig.isUnsupportedCountry &&
                  <InfoBox> Our calculator isn&apos;t yet optimised for {getCountryName(countryCode)}, so we&apos;ll use EU data for now! </InfoBox>
                }

                {/*<div className="flights-section cc-question">*/}
                {/*  <div className='cc-question-title'>Household</div>*/}
                {/*  <div className='cc-question-description'>Enter the number of people you live with</div>*/}

                {/*  <NumberPicker value={adults} onChange={(val) => setAdults(val)} questionConfig={questionsConfig.questions.adults} />*/}
                {/*  <NumberPicker value={children} onChange={(val) => setChildren(val)} questionConfig={questionsConfig.questions.children} />*/}
                {/*</div>*/}

                <CCSlider value={diet} onChange={handleQuestionChange} questionConfig={questionsConfig.questions.diet}></CCSlider>
                <CCSlider value={energy} onChange={handleQuestionChange} questionConfig={questionsConfig.questions.energy}>
                  <div className='d-flex pt-1'>
                    <Checkbox
                        className='child-size-20px'
                        checked={isRenewableEnergy}
                        onChange={(e) => {setIsRenewableEnergy(e.target.checked)}}
                    />
                    <div style={{paddingTop: '2px'}} className='font-bold cc-question-description pl-2'>I purchase carbon neutral energy</div>
                  </div>
                </CCSlider>
                <CCSlider value={driving} onChange={handleQuestionChange} questionConfig={questionsConfig.questions.driving}>
                  <Select
                  native
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      style={{fontSize: "12px"}}
                      value={carSize + '-' + carFuel}
                      onChange={(event) => handleQuestionChange(event, event.target.value,'carType', 'carType')}
                      >
                        {drivingData.vehicleTypes.map(type=> ( <option key={type.code()} value={type.code()}>{type.displayName()}</option> ))}
                    </Select> 
                </CCSlider>


                <CCSlider value={waste} onChange={handleQuestionChange} questionConfig={questionsConfig.questions.waste}></CCSlider>

                <div className="flights-section cc-question last">
                  <div className='cc-question-title'>Round Trip Flights</div>
                  <div className='cc-question-description'>
                    In the last year, excluding
                <Popover className="popover-question" content={businessFlightPopoverContent} title={<b>Flying for business</b>}>
                      <a>{` business `}</a>
                    </Popover>
                travel
              </div>

                  <NumberPicker value={quickFlights} onChange={(val) => setQuickFlights(val)} questionConfig={questionsConfig.questions.quickFlights} />
                  <NumberPicker value={shortFlights} onChange={(val) => setShortFlights(val)} questionConfig={questionsConfig.questions.shortFlights} />
                  <NumberPicker value={intermediateFlights} onChange={(val) => setIntermediateFlights(val)} questionConfig={questionsConfig.questions.intermediateFlights} />
                  <NumberPicker value={longFlights} onChange={(val) => setLongFlights(val)} questionConfig={questionsConfig.questions.longFlights} />
                </div>

              </div>
            }
            <div className="separator"></div>
            <div className="main-pane" style={{ paddingLeft: '35px', paddingTop: '20px', marginBottom: 15 }}>
              {countryCode && <Results answers={allAnswers} unavoidableCountryEmissionsPerPerson={questionsConfig?.questions.unavoidableCountryEmissions.multiplier} footprintHousehold={footprint} footprintOfOneAdult={footprintOfOneAdult} numberOfAdults={adults} numberOfChildren={children} countryCode={countryCode} hideRecommendedPanel={hideRecommendedSubscriptionPanel} merchantConfig={merchantConfig} isHideSave={isHideSave}></Results>}
            </div>
          </div>
        </article>

        <CountrySelectionModal
          showModal={isShowCountryModal}
          myLocation="/calculator"
          setLocationModalVisible={setShowCountryModal}
        />
      </main>

    </ThemeProvider>
  );
}

function mapStateToProps(state) {
  return {
    countryCodeFromRedux: state.countryResult && state.countryResult.country,
    userSubscription: state.subsList,
    personalFootprintData: state.personalFootprintData,
    currentUserCognito: state.currentUserCognito
  }
}

const mapDispatchToProps = (dispatch) => ({
  actions: {
    getAnswersByEmail: bindActionCreators(
      personalFootprintActions.getPersonalFootprint,
      dispatch
    )
  }
});

Calculator.propTypes = {
  country: PropTypes.string
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Calculator);